import React, { useState } from 'react';
import {
  InformationCircleIcon,
  Squares2X2Icon,
  PaintBrushIcon,
  InboxIcon,
  EnvelopeIcon,
  ChartBarIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/24/outline';

export type EditWebsiteTab = 'info' | 'sections' | 'theme' | 'analytics' | 'enquiries' | 'newsletter';

export const EDIT_WEBSITE_TABS: EditWebsiteTab[] = ['info', 'sections', 'theme', 'analytics', 'enquiries', 'newsletter'];

/**
 * "Sections" used to be split from a separate "Content" tab that just listed
 * the same sections again to pick one to fill in. They're one tab now — add
 * a section and write its content in the same place — but old links and
 * anything bookmarked with `?tab=content` should still land somewhere
 * sensible rather than falling back to "Website Info".
 */
export const LEGACY_TAB_REDIRECTS: Partial<Record<string, EditWebsiteTab>> = {
  content: 'sections',
};

interface TabConfig {
  id: EditWebsiteTab;
  label: string;
  shortLabel: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

const TABS: TabConfig[] = [
  { id: 'info', label: 'Website Info', shortLabel: 'Info', icon: InformationCircleIcon },
  { id: 'sections', label: 'Sections', shortLabel: 'Sections', icon: Squares2X2Icon },
  { id: 'theme', label: 'Theme', shortLabel: 'Theme', icon: PaintBrushIcon },
  { id: 'analytics', label: 'Analytics', shortLabel: 'Stats', icon: ChartBarIcon },
  { id: 'enquiries', label: 'Enquiries', shortLabel: 'Enquiries', icon: InboxIcon },
  { id: 'newsletter', label: 'Newsletter', shortLabel: 'Newsletter', icon: EnvelopeIcon },
];

/**
 * On mobile, only these "building" tabs sit directly in the bottom bar — the
 * ones a business flips between constantly while putting a site together.
 * Analytics/Enquiries/Newsletter are lower-frequency "reporting" tabs that
 * would otherwise overcrowd a 6-item bottom bar on small screens, so on
 * mobile they're tucked behind a "More" sheet instead. The desktop sidebar
 * is unaffected and still lists every tab flat.
 */
const MOBILE_PRIMARY_TAB_IDS: EditWebsiteTab[] = ['info', 'sections', 'theme'];
const MOBILE_MORE_TAB_IDS: EditWebsiteTab[] = ['analytics', 'enquiries', 'newsletter'];

interface EditWebsiteSidebarProps {
  activeTab: EditWebsiteTab;
  onChange: (tab: EditWebsiteTab) => void;
  /** Shown as a badge next to "Enquiries" when > 0. */
  unreadEnquiriesCount?: number;
}

const EditWebsiteSidebar: React.FC<EditWebsiteSidebarProps> = ({ activeTab, onChange, unreadEnquiriesCount = 0 }) => {
  const [moreOpen, setMoreOpen] = useState(false);

  const mobilePrimaryTabs = TABS.filter((tab) => MOBILE_PRIMARY_TAB_IDS.includes(tab.id));
  const moreTabs = TABS.filter((tab) => MOBILE_MORE_TAB_IDS.includes(tab.id));
  const isMoreTabActive = MOBILE_MORE_TAB_IDS.includes(activeTab);

  const renderTabButton = (tab: TabConfig) => {
    const Icon = tab.icon;
    const isActive = activeTab === tab.id;
    return (
      <button
        key={tab.id}
        onClick={() => onChange(tab.id)}
        className={`relative flex flex-1 md:flex-none flex-col md:flex-row items-center justify-center md:justify-start gap-0.5 md:gap-3 px-1 md:px-3 py-1.5 md:py-2.5 rounded-lg text-[11px] md:text-sm font-medium min-w-0 transition-colors ${
          isActive
            ? 'text-nova-sky bg-nova-sky/10 md:border-l-2 border-nova-sky'
            : 'text-nova-text3 hover:text-nova-text2 hover:bg-nova-bg3 md:border-l-2 border-transparent'
        }`}
      >
        <span className="relative flex-shrink-0">
          <Icon className="w-5 h-5" />
          {tab.id === 'enquiries' && unreadEnquiriesCount > 0 && (
            <span className="md:hidden absolute -top-1 -right-1.5 min-w-[1rem] h-4 px-1 flex items-center justify-center text-[10px] font-semibold rounded-full bg-nova-red text-white">
              {unreadEnquiriesCount > 9 ? '9+' : unreadEnquiriesCount}
            </span>
          )}
        </span>
        <span className="truncate max-w-full leading-tight">
          <span className="md:hidden">{tab.shortLabel}</span>
          <span className="hidden md:inline">{tab.label}</span>
        </span>
        {tab.id === 'enquiries' && unreadEnquiriesCount > 0 && (
          <span className="hidden md:flex ml-auto flex-shrink-0 min-w-[1.25rem] h-5 px-1.5 items-center justify-center text-xs font-semibold rounded-full bg-nova-red text-white">
            {unreadEnquiriesCount > 99 ? '99+' : unreadEnquiriesCount}
          </span>
        )}
      </button>
    );
  };

  return (
    <>
      {/* Desktop: a single vertical sidebar listing every tab. */}
      <nav className="hidden md:block md:static md:z-auto md:w-56 md:flex-shrink-0 md:border-r md:border-nova-border">
        <div className="flex flex-col px-3 py-4 gap-1">{TABS.map(renderTabButton)}</div>
      </nav>

      {/* Mobile: bottom bar with the primary "building" tabs + a "More" sheet for the rest. */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-nova-border bg-nova-bg2 pb-[env(safe-area-inset-bottom)]">
        <div className="flex items-stretch justify-around px-1 py-1 gap-0.5">
          {mobilePrimaryTabs.map(renderTabButton)}
          <button
            onClick={() => setMoreOpen(true)}
            className={`relative flex flex-1 flex-col items-center justify-center gap-0.5 px-1 py-1.5 rounded-lg text-[11px] font-medium min-w-0 transition-colors ${
              isMoreTabActive ? 'text-nova-sky bg-nova-sky/10' : 'text-nova-text3 hover:text-nova-text2 hover:bg-nova-bg3'
            }`}
          >
            <span className="relative flex-shrink-0">
              <EllipsisHorizontalIcon className="w-5 h-5" />
              {unreadEnquiriesCount > 0 && (
                <span className="absolute -top-0.5 -right-1 w-2.5 h-2.5 rounded-full bg-nova-red border-2 border-nova-bg2" />
              )}
            </span>
            <span className="truncate max-w-full leading-tight">More</span>
          </button>
        </div>
      </nav>

      {/* Mobile "More" sheet: Analytics / Enquiries / Newsletter live here on small screens. */}
      {moreOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex items-end" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMoreOpen(false)} />
          <div className="relative w-full bg-nova-bg2 rounded-t-2xl border-t border-nova-border pt-2 pb-4 pb-[calc(env(safe-area-inset-bottom)+1rem)]">
            <div className="w-10 h-1 bg-nova-border rounded-full mx-auto mb-2" />
            {moreTabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    onChange(tab.id);
                    setMoreOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-5 py-3 text-sm font-medium transition-colors ${
                    isActive ? 'text-nova-sky bg-nova-sky/10' : 'text-nova-text2 hover:bg-nova-bg3'
                  }`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  <span className="flex-1 text-left">{tab.label}</span>
                  {tab.id === 'enquiries' && unreadEnquiriesCount > 0 && (
                    <span className="flex-shrink-0 min-w-[1.25rem] h-5 px-1.5 flex items-center justify-center text-xs font-semibold rounded-full bg-nova-red text-white">
                      {unreadEnquiriesCount > 99 ? '99+' : unreadEnquiriesCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default EditWebsiteSidebar;
